import { Vector3 } from "three";
import { PrecisionUtils } from "../../infra3d";
import { FeatureStyle, GEOMETRYTYPE } from "../featuretree/internal";
import { MEASUREMODE } from "../internal";
import { MonoplotMeasurePoint } from "../measure/internal";
import { USERINPUTTYPE } from "../userinput/internal";
import { Tool } from "./internal";
/**
 * Draw Polygon Tool
 * @class DrawPolygonTool
 * @extends {Tool}
 */
export class ElevationTool extends Tool {
    constructor(scene, toolhandler, options, name, group) {
        super(scene, toolhandler);
        this.featurecollection = null;
        this.linestringfeature = null;
        this.multipointfeature = null;
        this.pointfeature = null;
        this.pos = null;
        this.snap_mx = null;
        this.snap_my = null;
        this.snappedpos = null;
        // event variables
        this.finishinputevent = null;
        this.resetinputevent = null;
        this.mousemoveeventnr = null;
        this.mouseouteventnr = null;
        this.name = name;
        this.group = group;
        const highlightlayerstyle = new FeatureStyle({
            stylename: "ElevationToolStyle_h",
            rules: [
                {
                    filter: "",
                    symbolizer: "point",
                    params: {
                        "font-marker": {
                            name: "cross",
                        },
                        size: 8,
                        stroke: [1, 0, 1, 0.6],
                    },
                },
                {
                    filter: "",
                    symbolizer: "line",
                    params: {
                        "stroke-width": 4,
                        stroke: [1, 1, 0, 1],
                    },
                },
            ],
        });
        //options - set the default values here....
        const defaultoptions = {
            highlightlayerstyle: highlightlayerstyle,
            startcbf: null,
            errorcbf: null,
            resetcbf: null,
            finishinput: USERINPUTTYPE.LEFTCLICK,
            resetinput: USERINPUTTYPE.RIGHTCLICK,
            continousdraw: false,
            maxdist: 30,
        };
        for (const property in defaultoptions) {
            this.options[property] = defaultoptions[property];
        }
        super.parseOptions(options);
        //create the drawing layers
        this.featurecollection = this.scene
            .getFeatureHandler()
            .createFeatureCollection(this.options.layerstyle, this.options.highlightlayerstyle, "mmElevationToolLineStringCollection_internal");
        this.linestringfeature = this.featurecollection.createFeature();
        this.linestringfeature.geometry = this.linestringfeature.createGeometry(GEOMETRYTYPE.LINESTRING);
        this.multipointfeature = this.featurecollection.createFeature();
        this.multipointfeature.geometry = this.multipointfeature.createGeometry(GEOMETRYTYPE.MULTIPOINT);
        this.pointfeature = this.featurecollection.createFeature();
        this.pointfeature.geometry = this.pointfeature.createGeometry(GEOMETRYTYPE.POINT);
        this.type = "ElevationTool";
    }
    /**
     * internal function - to activate tool call .enable
     * @method _enableTool
     * @protected
     */
    _enableTool() {
        this.scene.getCursorStyleHandler().setCursorStyle(this.options.activecursor);
        this._subscribeEvents();
        this.fire(ElevationTool.enabled, this.options["cbobject"]);
    }
    /**
     * internal function - to disable tool call .disable
     * @method _disableTool
     * @protected
     */
    _disableTool() {
        //if tool is not active there is nothing to disable...
        if (!this.enabled)
            return;
        this.scene.getCursorStyleHandler().setCursorStyle("default");
        this.fire(ElevationTool.disabled, this.options["cbobject"]);
        this._cleanUp();
        this._unsubscribeEvents();
    }
    _subscribeEvents() {
        if (this.options.continousdraw && this.mousemoveeventnr == null) {
            this.mousemoveeventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.MOUSEMOVE, this._onMouseMove, this);
        }
        this.mouseouteventnr = this.scene.userInputHandler.subscribeEvent(USERINPUTTYPE.MOUSEOUT, this._onMouseOut, this);
        if (this.options.finishinput != null && this.finishinputevent == null) {
            this.finishinputevent = this.scene.userInputHandler.subscribeEvent(this.options.finishinput, this._finishmeasurement, this);
        }
        if (this.options.resetinput != null && this.resetinputevent == null) {
            this.resetinputevent = this.scene.userInputHandler.subscribeEvent(this.options.resetinput, this.reset, this);
        }
        if (this.options.enablecbf != null) {
            this.on(ElevationTool.enabled, this.options.enablecbf);
        }
        if (this.options.disablecbf != null) {
            this.on(ElevationTool.disabled, this.options.disablecbf);
        }
        if (this.options.startcbf != null) {
            this.on(ElevationTool.started, this.options.startcbf);
        }
        if (this.options.continousdrawcbf != null) {
            this.on(ElevationTool.continousdraw, this.options.continousdrawcbf);
        }
        if (this.options.resetcbf != null) {
            this.on(ElevationTool.reset, this.options.resetcbf);
        }
        if (this.options.finishedcbf != null) {
            this.on(ElevationTool.finished, this.options.finishedcbf);
        }
        if (this.options.errorcbf != null) {
            this.on(ElevationTool.error, this.options.errorcbf);
        }
    }
    _unsubscribeEvents() {
        this.scene.userInputHandler.unSubscribeEvent(this.options.finishinput, this.finishinputevent);
        this.finishinputevent = null;
        this.scene.userInputHandler.unSubscribeEvent(this.options.resetinput, this.resetinputevent);
        this.resetinputevent = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.MOUSEMOVE, this.mousemoveeventnr);
        this.mousemoveeventnr = null;
        this.scene.userInputHandler.unSubscribeEvent(USERINPUTTYPE.MOUSEOUT, this.mouseouteventnr);
        this.mouseouteventnr = null;
        if (this.options.enablecbf != null) {
            this.off(ElevationTool.enabled, this.options.enablecbf);
        }
        if (this.options.disablecbf != null) {
            this.off(ElevationTool.disabled, this.options.disablecbf);
        }
        if (this.options.startcbf != null) {
            this.off(ElevationTool.started, this.options.startcbf);
        }
        if (this.options.continousdrawcbf != null) {
            this.off(ElevationTool.continousdraw, this.options.continousdrawcbf);
        }
        if (this.options.resetcbf != null) {
            this.off(ElevationTool.reset, this.options.resetcbf);
        }
        if (this.options.finishedcbf != null) {
            this.off(ElevationTool.finished, this.options.finishedcbf);
        }
        if (this.options.errorcbf != null) {
            this.off(ElevationTool.error, this.options.errorcbf);
        }
    }
    /**
     * internal function mousemove
     * @method _onMouseMove
     * @param evt
     * @private
     */
    _onMouseMove(evt) {
        if (this.pointfeature.ishidden) {
            this.pointfeature.show();
        }
        this._measureOnMouseMove(evt);
    }
    /**
     * internal function mouseout
     * @method _onMouseOut
     * @param evt
     * @private
     */
    _onMouseOut(evt) {
        this.pointfeature.hide();
        this.pointfeature.update(true);
    }
    _measureOnMouseMove(evt) {
        const mx = evt.offsetX;
        const my = evt.offsetY;
        this._checkSnaping(evt);
        //make a measurepoint in mousemove measuremode
        this.scene.getMeasureHandler().createMeasurePointInMeasureMode(this.scene.getCurrentGeoframe(), mx, my, this.options.maxdist, (pos) => {
            this.pos = pos;
            if (this.pos.warning) {
                this.fire(ElevationTool.error, this.options["cbobject"], this.pos.warntext, evt);
                this.scene.getCursorStyleHandler().setCursorStyle("not-allowed");
                return;
            }
            this.fire(ElevationTool.continousdraw, this.options["cbobject"], { mx: mx, my: my, pos: this.pos }, evt);
            this.scene.getCursorStyleHandler().setCursorStyle(this.options.activecursor);
            this.scene.getMeasureModeHandler().hideMeasureModeCursor();
            this.pointfeature.geometry.coordinates = [[this.pos.Easting, this.pos.Northing, this.pos.Height]];
            this.pointfeature.update(true);
        }, (msg) => {
            this.scene.getCursorStyleHandler().setCursorStyle("not-allowed");
            this.pointfeature.geometry.coordinates = [[0, 0, 0]];
            this.pointfeature.update(true);
        }, this.scene.getMeasureModeHandler().mouseMoveMeasureMode);
    }
    /**
     * internal function - Finish measurement
     * @method _finishmeasurement
     * @param evt
     * @private
     */
    _finishmeasurement(evt) {
        if (evt.isDragging) {
            this._measureOnMouseMove(evt);
        }
        else {
            const mx = evt.offsetX;
            const my = evt.offsetY;
            this.evt = evt; //store event temporary...
            this._checkSnaping(evt);
            if (this.snappedpos == null) {
                this.scene.getMeasureHandler().createMeasurePointInMeasureMode(this.scene.getCurrentGeoframe(), mx, my, this.options.maxdist, (pos) => {
                    if (!pos.warning)
                        this._posRequestSuccess(pos);
                    else {
                        setTimeout(() => {
                            this._subscribeEvents();
                        }, 10);
                    }
                }, (pos) => {
                    this._posRequestFailed(pos);
                }, MEASUREMODE.DEFINED_BY_SCENE);
            }
            else {
                this._posRequestSuccess(this.snappedpos);
            }
        }
    }
    _checkSnaping(evt) {
        const mx = evt.offsetX;
        const my = evt.offsetY;
        if (this.options.snaplayer && !evt.altKey) {
            const snapdataarray = this.scene.getFeatureHandler().snap(mx, my, this.options.snaplayer);
            //prevent self pickcing and filter non-pointy geometries
            for (let i = snapdataarray.length - 1; i >= 0; i--) {
                if (snapdataarray[i].geometry.type != GEOMETRYTYPE.MULTIPOINT &&
                    snapdataarray[i].geometry.type != GEOMETRYTYPE.POINT) {
                    snapdataarray.splice(i, 1);
                }
            }
            if (snapdataarray.length > 0) {
                const snapdata = snapdataarray[0];
                this.snap_mx = snapdata.found_mx;
                this.snap_my = snapdata.found_my;
                this.pos.Easting = snapdata.found_easting;
                this.pos.Northing = snapdata.found_northing;
                this.pos.Height = snapdata.found_height;
                this.pos.warning = false;
                this.snappedpos = this.pos;
            }
            else {
                this.snap_mx = null;
                this.snap_my = null;
                this.snappedpos = null;
            }
        }
    }
    _posRequestFailed(pos) {
        this.reset();
        this.scene.getMeasureHandler().abortMeasureSequence();
    }
    _posRequestSuccess(upperPos) {
        this.fire(ElevationTool.started, this.options["cbobject"], this.evt);
        if (upperPos.warning) {
            this.fire(ElevationTool.error, this.options["cbobject"], upperPos.warntext, this.evt);
            this._cleanUp();
        }
        else {
            const world = new Vector3(upperPos.Easting, upperPos.Northing, upperPos.Height);
            this.scene.getMeasureHandler().getElevationFromPosition3(world, 1, (X, Y, Z, id, quality) => {
                this._lowerPointFound(upperPos, X, Y, Z, this.evt, quality);
            }, this._failed, this, 0, this.scene.getCurrentGeoframe());
        }
        setTimeout(() => {
            this._subscribeEvents();
        }, 10);
    }
    /**
     * internal function - callback wich will be called as soon a lower points was found
     * @method _lowerPointFound
     * @param {MeasurePoint} upperPos
     * @param {number} X
     * @param {number} Y
     * @param {number} Z
     * @param {Object} evt
     * @param quality
     */
    _lowerPointFound(upperPos, X, Y, Z, evt, quality) {
        const distance = upperPos.Height - Z;
        if (distance < 0.0) {
            this._failed(evt);
            return;
        }
        const precDistance = this._calcPrecisionHeight(upperPos, new Vector3(X, Y, Z));
        const val1 = [upperPos.Easting, upperPos.Northing, upperPos.Height];
        const val2 = [X, Y, Z];
        if (this.options.quality) {
            const qualitycodedstyle_json = JSON.parse(this.options.layerstyle.toJSON());
            if (quality > 191) {
                qualitycodedstyle_json.rules[1].params.stroke = [0, 1, 0];
            }
            else if (quality > 127) {
                qualitycodedstyle_json.rules[1].params.stroke = [1, 1, 0];
            }
            else if (quality > 63) {
                qualitycodedstyle_json.rules[1].params.stroke = [1, 0, 0];
            }
            const qualitycodedstyle = new FeatureStyle(qualitycodedstyle_json);
            this.featurecollection.changeStyle(qualitycodedstyle, this.options.highlightlayerstyle);
        }
        this.linestringfeature.geometry.coordinates[0] = val1;
        this.linestringfeature.geometry.coordinates[1] = val2;
        this.multipointfeature.geometry.coordinates[0] = val1;
        this.multipointfeature.geometry.coordinates[1] = val2;
        this.featurecollection.update();
        if (this.linestringfeature.geometry.coordinates.length != 0) {
            const linestringfeature = this.linestringfeature.toGeoJSON();
            this.fire(ElevationTool.finished, this.options["cbobject"], {
                linestringfeature: linestringfeature,
                distance: distance,
                precDistance: precDistance.precision,
                precMode: precDistance.mode,
            }, evt);
        }
    }
    /**
     * internal function - callback which will be called if the lower point search failed
     * @method _failed
     * @private
     */
    _failed(evt) {
        this.fire(ElevationTool.error, this.options["cbobject"], "no data", evt);
        this._cleanUp();
    }
    /**
     * removes all drawing from the scene
     * @method _cleanUp
     * @private
     */
    _cleanUp() {
        this.scene.getMeasureHandler().abortMeasureSequence();
        this.linestringfeature.geometry.clear();
        this.multipointfeature.geometry.clear();
        this.pointfeature.geometry.clear();
        this.featurecollection.update();
    }
    /**
     * resets the tool and calls the aborted event
     * @method reset
     * @param {?Object} evt EventObject if called from userinput
     * @public
     */
    reset(evt) {
        this._cleanUp();
        this.fire(ElevationTool.reset, this.options["cbobject"], evt);
    }
    _calcPrecisionHeight(upperPos, lowerPos) {
        const mi = this.scene
            .getMeasureHandler()
            .worldToMapinfo(lowerPos)
            .filter((e) => {
            return e.sensorid === 0;
        });
        if (mi.length !== 1) {
            return {
                precision: 0.0,
                mode: null,
            };
        }
        const m = this.scene.getMeasureHandler().mapToMouse(mi[0].coords.x, mi[0].coords.y);
        const p = new MonoplotMeasurePoint(this.scene.getCurrentGeoframe(), m.x, m.y);
        return {
            precision: PrecisionUtils.heightLineLocal([upperPos, p]),
            mode: "local",
        };
    }
}
ElevationTool.enabled = "enabled";
ElevationTool.disabled = "disabled";
ElevationTool.started = "started";
ElevationTool.continousdraw = "continousdraw";
ElevationTool.reset = "reset";
ElevationTool.finished = "finished";
ElevationTool.error = "error";
//# sourceMappingURL=elevationtool.js.map